import React, {useRef} from 'react';

import {organizationType} from '@ThemesApp/common/structuredData/organizationType';
import NC_SCHEMA_DATA from '@ThemesApp/common/constants/schema/organization/nc';

import Base from '@/templates/Base/Base';

import GenericHeading from '@Themes/natucare/modules/GenericHeading/GenericHeading';
import Container from '@Themes/natucare/components/Container/Container';
import Article from '@Themes/natucare/modules/Article/Article';
import GenericBlog from '@Themes/natucare/modules/GenericBlog/GenericBlog';

import Seo from '@/components/Seo/Seo';

import {useSiteMetadata} from '@/hooks/useSiteMetadata';

import {getBreadcrumbs} from '@/templates/Blog/utils/getBreadcrumbs';
import {parsePosts} from '@/templates/Blog/utils/parsePosts';
import {parseContent} from '@/templates/Blog/utils/parseContent/parseContent';

import {useSelectionTooltip} from '@/hooks/useSelectionTooltip';
import {useCalculatorType} from "@/hooks/useCalculatorType";


import DATA from '@/data/blog-post.data';

import './BlogList.scss';

const BlogList = ({pageContext, data, postsModel, hrefLang}) => {
  const blogListWrapperRef = useRef();

  const siteMetadata = useSiteMetadata();
  useSelectionTooltip(blogListWrapperRef);
  useCalculatorType({blogListWrapperRef, data: pageContext.calculatorLists});

  const locale = `${pageContext.localeSlug && '/'}${pageContext.localeSlug || ''}`;
  const siteUrl = `${siteMetadata.siteUrl}${locale}`;
  const categorySlug = `/${data.slug}`;
  const categoryUrl = `${siteUrl}${categorySlug}`;
  const {locale: contextLocale, content, headerData, hasNoindex, footerData} = pageContext;

  const parsedContent = parseContent(content, pageContext);

  const posts = parsePosts({postsModel, category: data});
  

  const breadcrumbs = getBreadcrumbs(
    [
      {
        label: DATA[contextLocale].breadcrumbs.home,
        schemaLabel: '❤️',
        href: locale,
        schemaHref: siteUrl
      },
      {
        label: data.shortHeading || data.heading,
        schemaHref: `${siteUrl}${categorySlug}`
      }
    ]
  );

  return (
    <Base headerData={headerData} footerData={footerData} isKlaviyoPl={contextLocale === 'pl'} locale={contextLocale}>
      <Seo
        hasNoindex={hasNoindex}
        lang={contextLocale}
        title={data.seo.title}
        description={data.seo.description}
        hrefLang={hrefLang}
        vwo={data.options?.strapi_json_value.indexOf('enableVwo') !== -1}
        meta={[
          {
            property: 'entryID',
            content: data.strapi_id
          },
          {
            property: 'article:published_time',
            content: data.metaPublishedTime
          },
          {
            property: 'article:modified_time',
            content: data.metaUpdatedTime
          }
        ]}
        structuredData={{
          breadcrumbs: {
            itemListElement:
              breadcrumbs.map(item => ({
                name: item.schemaLabel,
                item: item.schemaHref
              }))
          },
          webPage: {
            url: categoryUrl,
            name: data.seo.title || data.heading,
            lastReviewed: data.metaUpdatedTime,
            reviewedBy: JSON.parse(organizationType(NC_SCHEMA_DATA))
          },
          siteUrl: siteUrl
        }}
      />
      <GenericHeading variant='Category' heading={data.heading} description={data.shortContent}
                      breadcrumbs={breadcrumbs}/>
      <div className='blogListWrapper' ref={blogListWrapperRef}>
        {parsedContent &&
          <Container classname='blogList'>
            <Article html={parsedContent}/>
          </Container>
        }
        {posts.length > 0 && <GenericBlog items={posts} variant='LightGray'/>}
      </div>
    </Base>
  );
};

export default BlogList;
